import { ICONS } from 'components/Icon';
import { Menu } from 'components/Menu';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirm } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { GENERICKEY_FINDINGSEARCH } from 'lib/riskbase';
import { without } from 'lodash';
import React from 'react';
import { ListRenderItem } from '@shopify/flash-list';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  SET_GENERIC,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectGeneric } from 'store/app/selectors';
import { selectAseSetupLibraryControlIdsAlphaOrdered } from 'store/remote/selectors';
import { ControlListItem } from './ListItems/ControlListItem';
import { List } from 'components/List';
import { PageEmpty } from 'components/PageEmpty';
import { PageHeader } from 'components/PageHeader';
import { PageToolbar } from 'components/PageToolbar';

const genericKeySearchString = GENERICKEY_FINDINGSEARCH({
  where: 'setupSuggestions',
  type: 'controls',
});

export const ControlsListScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYACTIONSLIST>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { hideIds, incomingValueKey } = route.params;

  const textFieldKeyRef = React.useRef(Math.random());
  const doAutoFocusRef = React.useRef<undefined | boolean>(undefined);
  doAutoFocusRef.current = doAutoFocusRef.current === undefined;

  const searchValue =
    (useSelector((state) =>
      selectGeneric(state, genericKeySearchString),
    ) as string) || '';
  const searchValueRef = React.useRef(searchValue);

  const controlIds = useSelector((state) =>
    selectAseSetupLibraryControlIdsAlphaOrdered(state, genericKeySearchString),
  );

  const filteredControlIds = React.useMemo(
    () =>
      hideIds.length
        ? controlIds.filter((id) => !hideIds.includes(id))
        : controlIds,
    [controlIds, hideIds],
  );

  const [checkedIds, setCheckedIds] = React.useState<number[]>([]);

  const handlePressSave = () => {
    dispatch(SET_INCOMING_VALUE({ key: incomingValueKey, value: checkedIds }));
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handlePressRow = React.useCallback((id: number) => {
    setCheckedIds((existing) =>
      existing.includes(id) ? without(existing, id) : [...existing, id],
    );
  }, []);

  const renderItem: ListRenderItem<
    ReturnType<typeof selectAseSetupLibraryControlIdsAlphaOrdered>[number]
  > = React.useCallback(
    ({ item, extraData }) => {
      return (
        <ControlListItem
          id={item}
          onPress={handlePressRow}
          checked={extraData.checkedIds.includes(item)}
        />
      );
    },
    [handlePressRow],
  );
  const handleSearch = React.useCallback(
    (search: string) => {
      searchValueRef.current = search;
      dispatch(
        SET_GENERIC({ key: genericKeySearchString, value: search, temp: true }),
      );
    },
    [dispatch],
  );

  const searchIncludes = React.useCallback(
    (searchText: string) =>
      searchValueRef.current.split(' ').includes(searchText),
    [],
  );

  const handleAddFilter = React.useCallback(
    (addFilter: string, removeFilter: string) => {
      textFieldKeyRef.current = Math.random();
      doAutoFocusRef.current = undefined;
      let currentValue = searchValueRef.current.split(' ');
      if (currentValue.length && removeFilter) {
        currentValue = without(searchValueRef.current.split(' '), removeFilter);
      }
      if (addFilter && !currentValue.includes(addFilter)) {
        currentValue.push(addFilter);
      }
      handleSearch(currentValue.join(' '));
    },
    [handleSearch],
  );

  const returnFilterOption = (label: string, add: string, remove: string) => ({
    text: label,
    subText: '',
    icon: searchIncludes(add) ? ICONS.CheckLight : ICONS.Blank,
    onPress: () =>
      searchIncludes(add)
        ? handleAddFilter('', add)
        : handleAddFilter(add, remove),
  });

  const hasSelection = checkedIds.length > 0;
  const handleClose = React.useCallback(() => {
    if (hasSelection) {
      confirm({
        text: 'Your selection will be lost if you continue',
        confirmText: 'Discard Selection',
        cancelText: 'Cancel',
        destructive: true,
        onConfirm: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [hasSelection, dispatch, confirm]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !hasSelection });
  }, [navigation, hasSelection]);
  useEsc(handleClose);

  return (
    <Page
      header={
        <>
          <PageHeader
            title="Link Controls"
            subtitle={pluralise(
              checkedIds.length,
              '{x} Selected',
              '{x} Selected',
              'None Selected',
            )}
            onPressCancelButton={() => dispatch(NAVIGATE_MODAL_BACK())}
            onPressSaveButton={checkedIds.length ? handlePressSave : null}
          />
          <PageToolbar
            onSearch={handleSearch}
            searchValue={searchValue}
            onSearchEsc={handleClose}
            searchPlaceholder="Search suggestions..."
          >
            <Menu
              iconButtonConfig={{
                icon: ICONS.SlidersUpLight,
              }}
              options={[
                'Control Filters',
                returnFilterOption('Is Linked', '+link', '-link'),
                returnFilterOption('Is Not Linked', '-link', '+link'),
                '-',
                returnFilterOption('Has Category', '+category', '-category'),
                returnFilterOption('No Category', '-category', '+category'),
                '-',
                returnFilterOption('Has Severity', '+severity', '-severity'),
                returnFilterOption('No Severity', '-severity', '+severity'),
                '-',
                returnFilterOption('Has Timescale', '+timescale', '-timescale'),
                returnFilterOption('No Timescale', '-timescale', '+timescale'),
              ]}
            />
          </PageToolbar>
        </>
      }
    >
      {filteredControlIds.length ? (
        <List
          data={filteredControlIds}
          keyExtractor={(item) => `${item}`}
          renderItem={renderItem}
          extraData={{ checkedIds }}
          ItemSeparatorComponent={undefined}
        />
      ) : (
        <PageEmpty
          icon={ICONS.RBPlectrumLight}
          text={'No controls available to link'}
        />
      )}
    </Page>
  );
};
